import { createSelector } from 'reselect';
import update from 'immutability-helper';
import {
	getCurrentErrand,
	getDomainBasicErrands,
	currentChatErrand,
	notMineSelector,
	collaborationInputs,
	areaAgentDataSelector,
	getCurrentErrandAreaData,
	replyEeAddress
} from './errand';
import { noSelector } from './common';
import {
	getValidDomainData
} from '../util';
import {
	LIGHT_NOT_MINE,
	LIGHT_NO_EE,
	LIGHT_READY,
	EXPAND_BEFORE,
	EXPAND_AFTER
} from '../../common/v5/constants';

const getEEQueriesData = state => {
	const e = state.app.errand;
	if(e.fetchExternalQueries.data) {
		return e.fetchExternalQueries.data;
	}
};

const getEEQueriesTargets = createSelector(
	getEEQueriesData,
	queriesData => {
		if(queriesData) {
			let data = [];
			if(queriesData.externalRecipients &&
				queriesData.externalRecipients.length) {
				$.each(queriesData.externalRecipients, (i,v) => {
					if(v.name) {
						data.push(v);
					} else {
						data.push(update(v, {name: {$set: v.email}}));
					}
				});
			}
			if(queriesData.internalRecipients &&
				queriesData.internalRecipients.length) {
				data.push(...queriesData.internalRecipients);
			}
			if(data.length) {
				return data;
			}
		}
	}
);

const getTotalAnnswersFromQuery = createSelector(
	getEEQueriesData,
	queriesData => {
		if(queriesData) {
			return queriesData.answered;
		}
	}
);

const getCurrentErrandCollaboration = createSelector(
	currentChatErrand,
	getCurrentErrand,
	getDomainBasicErrands,
	(chat, eid, domain) => {
		if (chat) {
			return chat.errand.data.collaboration;
		}
		const rawData = domain.byId[eid];
		if(rawData) {
			return rawData.data.collaboration;
		}
	}
);

const getSimpleCollaboration = state => {
	return state.app.errand.currentErrand.collaboration;
};

export const hasCollaborationMemo = noSelector(
	getSimpleCollaboration,
	collaboration => !!collaboration
);

const getCollaboration = createSelector(
	[
		getCurrentErrandCollaboration,
		getSimpleCollaboration
	],
	(col, simpleCol) => {
		if(!col || !simpleCol) {
			return simpleCol;
		}
		const {queries: denom, answers: num, status} = col;
		return update(simpleCol, {$merge: {denom, num, status}});
	}
);

const getTotalQueries = createSelector(
	getCollaboration,
	collaboration => {
		if(collaboration) {
			return collaboration.denom;
		}
	}
);

const getTotalReplies = createSelector(
	getCollaboration,
	collaboration => {
		if(collaboration) {
			return collaboration.num;
		}
	}
);

const getCollaborationStatus = createSelector(
	getCollaboration,
	collaboration => {
		if(collaboration) {
			return collaboration.status;
		}
		return '';
	}
);

const notReadyEESummary = {
	targets: [],
	status: '',
	queries: 0,
	answers: 0
};

export const getEESummarySelector = createSelector(
	[
		getEEQueriesTargets,
		getCollaborationStatus,
		getTotalQueries,
		getTotalReplies,
		getTotalAnnswersFromQuery
	],
	(targets, status, queries, replies, answers) => {
		if(!targets || typeof queries === 'undefined' ||
			typeof replies === 'undefined' || typeof answers === 'undefined') {
			return notReadyEESummary;
		}
		if(replies !== answers) {
			console.log('dbg: it happen inconsistent answer:', replies,
				answers);
		}
		return {targets, status, queries, answers};
	}
);

const getDomainEEList = state => state.domain.externalExpertList;

const getCurrentErrandEEList = (state, props) => {
	const list = getDomainEEList(state);
	if(list) {
		const currentID = list.byId[props.eid];
		if(currentID) {
			return currentID.list;
		}
	}
};

const defLightCondition = {
	condition: LIGHT_NO_EE
};

export const getCollaborationLightSelector = createSelector(
	[
		getCurrentErrandCollaboration,
		notMineSelector
	],
	(collaborate, notMine) => {
		if(!collaborate) {
			return defLightCondition;
		} else if(notMine) {
			return update(defLightCondition, {condition: {
				$set: LIGHT_NOT_MINE}});
		}
		return update(defLightCondition, {
			condition: {$set: LIGHT_READY},
			on: {$set: collaborate.light}
		});
	}
);

export const getDomainEEThreads = state => state.domain.externalExpertThreads;

export const getCollaborationListSelector = createSelector(
	[
		getCollaboration,
		getCurrentErrandEEList,
		getDomainEEThreads
	],
	(collaborate, list, threads) => {
		let eeList = [];
		if(typeof collaborate !== 'undefined' && collaborate.eeList) {
			if(list) {
				$.each(list, (i,v) => {
					const id = v.id,
						threadData = getValidDomainData(threads, id);
					if(threadData) {
						eeList.push({type: EXPAND_AFTER, data: threadData, id, header: v});
					} else {
						eeList.push({type: EXPAND_BEFORE, data: v, id});
					}
				});
			}
		}
		return eeList;
	}
);

export const collaborationGroupName = createSelector(
	collaborationInputs
	, areaAgentDataSelector
	, (c, agents) => {
		let sLen = c.selectedAgents.length,
			title = I("No agent selected");
		if(sLen > 0 && sLen === agents.length) {
			return I('All');
		} else if(sLen > 1) {
			return I('Multiple');
		} else if(sLen === 1) {
			$.each(agents, (i,v) => {
				if(v.Id === c.selectedAgents[0]) {
					title = v.Name;
					return false;
				}
			});
			return title;
		}
		return title;
	}
);

// Selector to create a map of email addresses to their suggested answers/question types
export const expertAnswersMapSelector = createSelector(
	[
		getCurrentErrandAreaData,
		replyEeAddress,
		getCurrentErrandEEList,
		getDomainEEThreads
	],
	(areaData, eeAddresses, list, threads) => {
		const answersMap = {};
		
		// First, get question types from regular address book
		if (areaData && areaData.addressbook_addresses && areaData.addressbook_addresses.length > 0) {
			$.each(areaData.addressbook_addresses, (i, addressEntry) => {
				// Check if this entry has a question type (suggested question)
				if (addressEntry.questionType) {
					const formattedQuestionType = addressEntry.questionType
						.replace(/\r\n/g, '<br>')
						.replace(/\n/g, '<br>')
						.replace(/\r/g, '<br>');
					
					// Address book entries can have multiple email addresses in addressBookEntries
					if (addressEntry.addressBookEntries && addressEntry.addressBookEntries.length > 0) {
						$.each(addressEntry.addressBookEntries, (j, entry) => {
							if (entry.address) {
								answersMap[entry.address] = {
									content: '<div><strong>' + I('Note') + ':</strong><p>' + formattedQuestionType + '</p></div>',
									source: 'addressbook',
									name: addressEntry.value || entry.address
								};
							}
						});
					} else if (addressEntry.id) {
						// Old format: direct email address as id
						answersMap[addressEntry.id] = {
							content: '<div><strong>' + I('Note') + ':</strong><p>' + formattedQuestionType + '</p></div>',
							source: 'addressbook',
							name: addressEntry.value || addressEntry.id
						};
					}
				}
			});
		}
		
		// Second, get question types from Expert in Area list (can override address book)
		if (eeAddresses && eeAddresses.length > 0) {
			$.each(eeAddresses, (i, expert) => {
				if (expert.id && expert.questionType) {
					const formattedQuestionType = expert.questionType
						.replace(/\r\n/g, '<br>')
						.replace(/\n/g, '<br>')
						.replace(/\r/g, '<br>');
					
					answersMap[expert.id] = {
						content: '<div><strong>' + I('Note') + ':</strong><p>' + formattedQuestionType + '</p></div>',
						source: 'expertInArea',
						name: expert.name
					};
				}
			});
		}
		
		if (list && threads) {
			$.each(list, (i, thread) => {
				const threadID = thread.id;
				const threadData = getValidDomainData(threads, threadID);
				
				if (threadData && threadData.message && threadData.message.length > 0) {
					const answers = threadData.message.filter(msg => msg.answer);
					
					$.each(answers, (j, answer) => {
						let emailAddress = null;
						
						if (answer.from && answer.from.email) {
							emailAddress = answer.from.email;
						} else if (answer.fromInternal && answer.fromInternal.email) {
							emailAddress = answer.fromInternal.email;
						}
						
						if (emailAddress) {
							const content = answer.htmlBody || answer.body || '';
							
							if (!answersMap[emailAddress] || answersMap[emailAddress].source === 'addressbook' ||
								(answer.date && answersMap[emailAddress].date && answer.date > answersMap[emailAddress].date)) {
								answersMap[emailAddress] = {
									content: content,
									date: answer.date,
									source: 'thread',
									name: (answer.from && answer.from.name) || (answer.fromInternal && answer.fromInternal.name) || emailAddress
								};
							}
						}
					});
				}
			});
		}

		const simpleMap = {};
		$.each(answersMap, (email, data) => {
			simpleMap[email] = data.content;
		});

		return simpleMap;
	}
);
